import { ref } from "vue";
import { useRouter } from "vue-router";
import { useUsersStore } from "@/stores/v2";
import AuthService from "@/services/v2/auth";
import { ApiError } from "@/api/v2/api";

interface FormState {
  username: string;
  password: string;
  errors: {
    username: string[];
    password: string[];
    general: string[];
  };
  isSubmitting: boolean;
}

export function useLoginForm() {
  const router = useRouter();
  const userStore = useUsersStore();

  const formState = ref<FormState>({
    username: "",
    password: "",
    errors: {
      username: [],
      password: [],
      general: [],
    },
    isSubmitting: false,
  });

  const clearErrors = () => {
    formState.value.errors = {
      username: [],
      password: [],
      general: [],
    };
  };

  const handleApiError = (errorData: ApiError) => {
    if (errorData?.status === "failure") {
      formState.value.errors.general = [errorData.message];
      return;
    }

    if (errorData?.errors) {
      if (errorData.errors.user_name) {
        formState.value.errors.username = errorData.errors.user_name;
      }
      if (errorData.errors.password) {
        formState.value.errors.password = errorData.errors.password;
      }

      if (
        errorData.message &&
        !errorData.errors.user_name &&
        !errorData.errors.password
      ) {
        formState.value.errors.general = [errorData.message];
      }
    } else if (errorData?.message) {
      formState.value.errors.general = [errorData.message];
    } else {
      formState.value.errors.general = [
        "An unexpected error occurred. Please try again.",
      ];
    }
  };

  const handleSubmit = async () => {
    formState.value.isSubmitting = true;
    clearErrors();

    try {
      const userData = await AuthService.login({
        user_name: formState.value.username,
        password: formState.value.password,
      });

      userStore.login(userData.user);
      localStorage.setItem("user", JSON.stringify(userData));

      await router.push({name: "DashboardSites"});
    } catch (error: unknown) {
      const err = error as ApiError;
      handleApiError({
        status: err.status?.toString() ?? "error",
        message: err.message ?? "Network error",
      });
    } finally {
      formState.value.isSubmitting = false;
    }
  };

  return {
    formState,
    handleSubmit,
  };
}
